import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { OrderExchangeData, OrderExchangeErrors } from '../../types';
import { isDefined, Loadable } from '../../utils';

export const useOrderExchangeErrorsCommon = ({
  balances,
  balancesByCurrency,
  exchangeRate,
  funding,
  fundingMode,
  selectedCurrency,
  isCurrencyExchangeActive,
  isEnabled,
}: OrderExchangeData): OrderExchangeErrors => {
  const isExchangeUnavailable = useMemo<boolean>(() => {
    return !Loadable.getOrElse(isCurrencyExchangeActive, true);
  }, [isCurrencyExchangeActive]);

  const isSelectedCurrencyUnavailable = useMemo<boolean>(() => {
    return (
      isDefined(selectedCurrency) &&
      Loadable.hasValue(balancesByCurrency) &&
      !isDefined(balancesByCurrency[selectedCurrency])
    );
  }, [balancesByCurrency, selectedCurrency]);

  const isSelectedCurrencyBalanceInsufficient = useMemo<boolean>(() => {
    if (!isDefined(selectedCurrency) || !isDefined(fundingMode)) {
      return false;
    }
    return Loadable.getOrElse(
      Loadable.merge([funding, balancesByCurrency, exchangeRate], (funding, balancesByCurrency, exchangeRate) => {
        // If the exchange rate is not for the selected currency, we cannot determine if the balance is insufficient.
        if (exchangeRate.sourceCurrency !== selectedCurrency) {
          return false;
        }

        const balance = balancesByCurrency[selectedCurrency];
        if (!isDefined(balance)) {
          return false;
        }
        const amountInTargetCurrency = fundingMode === 'fullAmount' ? funding.remaining.total : funding.missingAmount;
        const balanceInTargetCurrency = new BigNumber(balance.investableAmount ?? 0)
          .times(new BigNumber(1).dividedBy(exchangeRate.exchangeRate))
          .toNumber();
        return balanceInTargetCurrency < amountInTargetCurrency;
      }),
      false,
    );
  }, [balancesByCurrency, exchangeRate, funding, fundingMode, selectedCurrency]);

  const isBalanceOrFundingLoadFailed = useMemo<boolean>(() => {
    return balances.hasError || funding.hasError;
  }, [balances, funding.hasError]);

  const isWithoutUseableCurrencyBalance = useMemo<boolean>(() => {
    return Loadable.hasValue(balances) && balances.length === 0;
  }, [balances]);

  const hasExchangeError = useMemo<boolean>(() => {
    return (
      isEnabled &&
      (isSelectedCurrencyBalanceInsufficient ||
        isWithoutUseableCurrencyBalance ||
        isSelectedCurrencyUnavailable ||
        isBalanceOrFundingLoadFailed)
    );
  }, [
    isEnabled,
    isSelectedCurrencyBalanceInsufficient,
    isWithoutUseableCurrencyBalance,
    isSelectedCurrencyUnavailable,
    isBalanceOrFundingLoadFailed,
  ]);

  return {
    isExchangeUnavailable,
    isSelectedCurrencyBalanceInsufficient,
    isSelectedCurrencyUnavailable,
    isBalanceOrFundingLoadFailed,
    isWithoutUseableCurrencyBalance,
    hasExchangeError,
  };
};
